import { GridApi } from 'ag-grid-community';
import { Component, ViewChild, inject } from '@angular/core';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { AlertGridOptionsService } from '../alert-columns/alert-column-definition/alert-grid-options.service';
import { AlertApiClientService } from '../services/alert-api-client.service';

export interface OrderActionsDialogData {
  watchAlerts: { id: string; title: string }[];
  orderNumber: string;
  sourceAlertId: string;
  action?: 'copy' | 'move';
  params?: any;
  parentGridApi?: GridApi;
}

@Component({
  selector: 'app-order-actions-dialog',
  templateUrl: './order-actions-dialog.component.html',
  styleUrls: ['./order-actions-dialog.component.scss'],
  standalone: false,
})
export class OrderActionsDialogComponent {
  dialogRef = inject<MatDialogRef<OrderActionsDialogComponent>>(MatDialogRef);
  data = inject<OrderActionsDialogData>(MAT_DIALOG_DATA);
  private alertApiClientService = inject(AlertApiClientService);
  private snackbarService = inject(SnackbarService);
  private alertGridOptionsService = inject(AlertGridOptionsService);

  public selectedWatchAlert = '';
  public searchText = '';
  public filterText = '';
  @ViewChild(MatAutocompleteTrigger)
  public autocompleteTrigger?: MatAutocompleteTrigger;

  highlightMatch(text: string, search: string): string {
    if (!search) {
      return text;
    }
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.replace(
      new RegExp(escaped, 'gi'),
      (match) => `<b>${match}</b>`,
    );
  }
  displayWatchAlert = (value?: string | null): string => {
    if (!value) {
      return '';
    }
    const match = this.data.watchAlerts.find((alert) => alert.id === value);
    return match?.title ?? value;
  };

  private get availableWatchAlerts(): { id: string; title: string }[] {
    const sourceId = this.data.sourceAlertId;
    if (!sourceId) {
      return this.data.watchAlerts;
    }
    return this.data.watchAlerts.filter(
      (alert) => String(alert.id) !== String(sourceId),
    );
  }

  get filteredWatchAlerts(): { id: string; title: string }[] {
    const text = this.filterText.trim().toLowerCase();
    const alerts = this.availableWatchAlerts;
    if (!text) {
      return alerts;
    }
    return alerts.filter((alert) => alert.title.toLowerCase().includes(text));
  }

  private get actionType(): 'copy' | 'move' {
    return this.data.action === 'move' ? 'move' : 'copy';
  }

  get actionIcon(): string {
    return this.actionType === 'move' ? 'drive_file_move' : 'copy_all';
  }

  get actionTooltip(): string {
    return this.actionType === 'move'
      ? 'Move to selected alert'
      : 'Copy to selected alert';
  }

  onSearchTextChange(value: string): void {
    if (this.isAlertIdentifier(value)) {
      return;
    }
    this.filterText = value;
    this.selectedWatchAlert = '';
    if (value && !this.autocompleteTrigger?.panelOpen) {
      this.autocompleteTrigger?.openPanel();
    }
  }

  openDropdown(): void {
    this.autocompleteTrigger?.openPanel();
  }

  onAction(): void {
    this.performOrderAction(this.actionType);
  }

  onCopy(): void {
    this.performOrderAction('copy');
  }

  onMove(): void {
    this.performOrderAction('move');
  }

  onWatchAlertSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedWatchAlert = event.option.value;
    this.searchText = event.option.value;
    this.filterText = '';
  }

  private performOrderAction(action: 'copy' | 'move'): void {
    if (!this.selectedWatchAlert) {
      return;
    }

    if (!this.data.orderNumber) {
      this.dialogRef.close({ selectedWatchAlert: this.selectedWatchAlert });
      return;
    }

    const targetAlert = this.data.watchAlerts.find(
      (watchAlert) => watchAlert.id === this.selectedWatchAlert,
    );
    const targetAlertTitle = targetAlert?.title || 'selected alert';
    this.dialogRef.close({ selectedWatchAlert: this.selectedWatchAlert });

    const action$ =
      action === 'move'
        ? this.alertApiClientService.moveOrderBetweenWatchAlerts(
            this.data.sourceAlertId,
            this.selectedWatchAlert,
            this.data.orderNumber,
          )
        : this.alertApiClientService.copyOrderToWatchAlert(
            this.selectedWatchAlert,
            this.data.orderNumber,
          );

    const successVerb = action === 'move' ? 'Moved' : 'Copied';

    action$.subscribe({
      next: () => {
        this.snackbarService.openHtmlSnackBar(
          [
            { text: `${successVerb} ` },
            { text: '1', bold: true },
            { text: ` order to "${targetAlertTitle}"` },
          ],
          'Dismiss',
        );
        if (action === 'move') {
          this.removeRowFromSourceGrid();
        }
        this.alertGridOptionsService.refreshGrid();
      },
    });
  }

  private removeRowFromSourceGrid(): void {
    const gridParams = this.data.params;
    if (gridParams?.api && gridParams?.data) {
      gridParams.api.applyTransaction({ remove: [gridParams.data] });
    }
  }

  private isAlertIdentifier(value: string): boolean {
    return this.data.watchAlerts.some((alert) => alert.id === value);
  }
}
